import React, { useState, useEffect } from 'react';
import { 
  ClipboardList,
  Send,
  CheckCircle,
  Clock,
  User,
  Mail,
  Calendar,
  Filter,
  Search,
  Download,
  Eye,
  BarChart3,
  TrendingUp,
  Award,
  AlertCircle,
  FileText,
  Plus,
  X,
  ChevronDown,
  ChevronRight,
  Settings
} from 'lucide-react';
import SurveyQuestionManager from './SurveyQuestionManager';

const Survey = ({ 
  candidates = [], 
  currentUser,
  API_BASE_URL,
  success,
  error 
}) => {
  const [mainTab, setMainTab] = useState('surveys');
  const [activeTab, setActiveTab] = useState('onboarding');
  const [surveys, setSurveys] = useState([]);
  const [filteredSurveys, setFilteredSurveys] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [surveyStats, setSurveyStats] = useState({
    onboarding: { sent: 0, completed: 0, pending: 0, responseRate: 0 },
    checkin: { sent: 0, completed: 0, pending: 0, responseRate: 0 },
    offboarding: { sent: 0, completed: 0, pending: 0, responseRate: 0 }
  });

  // Survey type configurations with improved terminology
  const surveyTypes = {
    onboarding: {
      id: 'onboarding',
      title: 'Initial Onboarding Survey',
      shortTitle: 'Onboarding',
      description: 'Capture first impressions and initial experience',
      icon: Award,
      gradient: 'from-blue-600 to-cyan-600',
      color: 'blue',
      bgGradient: 'from-blue-50 to-cyan-50',
      timing: 'Sent within first week of onboarding'
    },
    checkin: {
      id: 'checkin',
      title: 'Performance Check-in Survey',
      shortTitle: 'Check-in',
      description: 'Monitor ongoing experience and satisfaction',
      icon: TrendingUp,
      gradient: 'from-purple-600 to-indigo-600',
      color: 'purple',
      bgGradient: 'from-purple-50 to-indigo-50',
      timing: 'Sent quarterly for active candidates'
    },
    offboarding: {
      id: 'offboarding',
      title: 'Offboarding Feedback Survey',
      shortTitle: 'Offboarding',
      description: 'Gather insights from departing candidates',
      icon: FileText,
      gradient: 'from-orange-600 to-red-600',
      color: 'orange',
      bgGradient: 'from-orange-50 to-red-50',
      timing: 'Sent upon project completion or exit'
    }
  };

  // Fetch surveys from backend
  useEffect(() => {
    if (mainTab === 'surveys') {
      fetchSurveys();
    }
  }, [mainTab]);

  // Filter surveys based on active tab and filters
  useEffect(() => {
    let filtered = surveys.filter(survey => survey.survey_type === activeTab);
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(survey => survey.status === statusFilter);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(survey => 
        survey.candidate_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        survey.candidate_email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredSurveys(filtered);
    calculateStats();
  }, [surveys, activeTab, statusFilter, searchQuery]);

  const fetchSurveys = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/surveys.php?action=getAll`);
      const data = await response.json();
      
      if (data.success) {
        setSurveys(data.surveys || []);
      } else {
        error('Failed to fetch surveys');
      }
    } catch (err) {
      console.error('Error fetching surveys:', err);
      error('Error loading surveys');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateStats = () => {
    const stats = {
      onboarding: { sent: 0, completed: 0, pending: 0, responseRate: 0 },
      checkin: { sent: 0, completed: 0, pending: 0, responseRate: 0 },
      offboarding: { sent: 0, completed: 0, pending: 0, responseRate: 0 }
    };

    surveys.forEach(survey => {
      const type = survey.survey_type;
      if (stats[type]) {
        stats[type].sent++;
        if (survey.status === 'completed') {
          stats[type].completed++;
        } else if (survey.status === 'pending') {
          stats[type].pending++;
        }
      }
    });

    // Calculate response rates
    Object.keys(stats).forEach(type => {
      if (stats[type].sent > 0) {
        stats[type].responseRate = Math.round((stats[type].completed / stats[type].sent) * 100);
      }
    });

    setSurveyStats(stats);
  };

  const handleSendSurvey = async () => {
    if (selectedCandidates.length === 0) {
      error('Please select at least one candidate');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/surveys.php?action=send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          survey_type: activeTab,
          candidate_ids: selectedCandidates,
          sent_by: currentUser?.id
        })
      });

      const data = await response.json();
      
      if (data.success) {
        success(`Survey sent to ${selectedCandidates.length} candidate(s)`);
        setShowSendModal(false);
        setSelectedCandidates([]);
        fetchSurveys();
      } else {
        error(data.message || 'Failed to send survey');
      }
    } catch (err) {
      console.error('Error sending survey:', err);
      error('Error sending survey');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
      expired: { bg: 'bg-red-100', text: 'text-red-800', label: 'Expired' }
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getEligibleCandidates = () => {
    // Filter candidates based on survey type
    let filtered;
    switch(activeTab) {
      case 'onboarding':
        // Recently onboarded candidates (within last 30 days)
        filtered = candidates.filter(c => c.status === 'Active');
        break;
      case 'checkin':
        // Active candidates
        filtered = candidates.filter(c => c.status === 'Active');
        break;
      case 'offboarding':
        // Candidates in rolloff or completed status
        filtered = candidates.filter(c => c.status === 'Roll-off' || c.status === 'Completed');
        break;
      default:
        filtered = candidates;
    }
    
    return filtered;
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 border border-slate-200">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <ClipboardList className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Survey Management</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Track candidate feedback across their journey
                </p>
              </div>
            </div>
            
            {/* Main Tab Navigation */}
            <div className="flex items-center space-x-2 mt-4">
              <button
                onClick={() => setMainTab('surveys')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  mainTab === 'surveys'
                    ? 'bg-white text-slate-900 shadow-md border-2 border-blue-200'
                    : 'text-slate-600 hover:bg-white/50'
                }`}
              >
                Survey List
              </button>
              {(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && (
                <button
                  onClick={() => setMainTab('settings')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    mainTab === 'settings'
                      ? 'bg-white text-slate-900 shadow-md border-2 border-blue-200'
                      : 'text-slate-600 hover:bg-white/50'
                  }`}
                >
                  <Settings className="w-4 h-4" />
                  <span>Question Settings</span>
                </button>
              )}
            </div>
          </div>
          
          {mainTab === 'surveys' && (currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && (
            <button
              onClick={() => setShowSendModal(true)}
              className="flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all font-medium shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transform hover:scale-105"
            >
              <Plus className="w-4 h-4 mr-2" />
              Send Survey
            </button>
          )}
        </div>

        {/* Survey Type Tabs - Only show in surveys view */}
        {mainTab === 'surveys' && (
          <div className="mt-6 flex flex-wrap gap-3">
            {Object.values(surveyTypes).map((type) => {
              const Icon = type.icon;
              const isActive = activeTab === type.id;
              const stats = surveyStats[type.id];
              
              return (
                <button
                  key={type.id}
                  onClick={() => setActiveTab(type.id)}
                  className={`relative flex-1 min-w-[200px] p-4 rounded-xl transition-all duration-300 ${
                    isActive
                      ? `bg-gradient-to-br ${type.bgGradient} border-2 border-${type.color}-300 shadow-lg`
                      : 'bg-white border-2 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className={`w-10 h-10 bg-gradient-to-br ${type.gradient} rounded-lg flex items-center justify-center ${
                        isActive ? 'ring-2 ring-offset-2 ring-' + type.color + '-400' : ''
                      }`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-slate-900 text-sm">{type.shortTitle}</h3>
                        <p className="text-xs text-slate-600 mt-0.5">{stats.sent} sent</p>
                      </div>
                    </div>
                    
                    {stats.responseRate > 0 && (
                      <div className="text-right">
                        <div className={`text-lg font-bold ${isActive ? 'text-' + type.color + '-600' : 'text-slate-900'}`}>
                          {stats.responseRate}%
                        </div>
                        <div className="text-xs text-slate-500">response</div>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Content Area - Conditional based on mainTab */}
      {mainTab === 'surveys' ? (
        <>
          {/* Current Survey Type Info */}
          <div className={`bg-gradient-to-br ${surveyTypes[activeTab].bgGradient} rounded-xl p-5 border-2 border-${surveyTypes[activeTab].color}-200`}>
            <div className="flex items-start space-x-4">
              <div className={`w-12 h-12 bg-gradient-to-br ${surveyTypes[activeTab].gradient} rounded-xl flex items-center justify-center flex-shrink-0`}>
                {React.createElement(surveyTypes[activeTab].icon, { className: "w-6 h-6 text-white" })}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900">{surveyTypes[activeTab].title}</h3>
                <p className="text-sm text-slate-700 mt-1">{surveyTypes[activeTab].description}</p>
                <div className="flex items-center space-x-2 mt-2">
                  <Clock className="w-4 h-4 text-slate-600" />
                  <span className="text-xs text-slate-600 font-medium">{surveyTypes[activeTab].timing}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by candidate name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm font-medium"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Sent', value: surveyStats[activeTab].sent, icon: Send, color: 'blue' },
              { label: 'Completed', value: surveyStats[activeTab].completed, icon: CheckCircle, color: 'green' },
              { label: 'Pending', value: surveyStats[activeTab].pending, icon: Clock, color: 'yellow' },
              { label: 'Response Rate', value: `${surveyStats[activeTab].responseRate}%`, icon: BarChart3, color: 'purple' }
            ].map((stat, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 border-2 border-slate-200 hover:border-slate-300 transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 font-medium">{stat.label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 bg-gradient-to-br from-${stat.color}-100 to-${stat.color}-200 rounded-xl flex items-center justify-center`}>
                    <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Surveys List */}
          <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b-2 border-slate-200">
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Candidate
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Sent Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Completed Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {isLoading ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                          <p className="text-sm text-slate-600">Loading surveys...</p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredSurveys.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                        <p className="text-slate-600 font-medium">No surveys found</p>
                        <p className="text-sm text-slate-500 mt-1">Try adjusting your filters or send a new survey</p>
                      </td>
                    </tr>
                  ) : (
                    filteredSurveys.map((survey) => (
                      <tr key={survey.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center">
                              <User className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">{survey.candidate_name}</p>
                              <p className="text-sm text-slate-500">{survey.candidate_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2 text-sm text-slate-600">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(survey.sent_date).toLocaleDateString()}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(survey.status)}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-slate-600">
                            {survey.completed_date ? new Date(survey.completed_date).toLocaleDateString() : '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            {survey.status === 'completed' && (
                              <button
                                onClick={() => {
                                  setSelectedSurvey(survey);
                                  setShowResponseModal(true);
                                }}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="View Response"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            )}
                            {survey.status === 'pending' && (
                              <button
                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                                title="Resend Survey"
                              >
                                <Send className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Send Survey Modal */}
          {showSendModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
                <div className={`bg-gradient-to-r ${surveyTypes[activeTab].gradient} p-6`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {React.createElement(surveyTypes[activeTab].icon, { className: "w-6 h-6 text-white" })}
                      <h3 className="text-xl font-bold text-white">
                        Send {surveyTypes[activeTab].shortTitle} Survey
                      </h3>
                    </div>
                    <button
                      onClick={() => {
                        setShowSendModal(false);
                        setSelectedCandidates([]);
                      }}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                  <p className="text-sm text-slate-600 mb-4">
                    Select candidates to send the {surveyTypes[activeTab].shortTitle.toLowerCase()} survey
                  </p>

                  {getEligibleCandidates().length === 0 ? (
                    <div className="text-center py-12">
                      <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium">No eligible candidates found</p>
                      <p className="text-sm text-slate-500 mt-2">
                        {activeTab === 'onboarding' && 'No Active candidates available for onboarding survey'}
                        {activeTab === 'checkin' && 'No Active candidates available for check-in survey'}
                        {activeTab === 'offboarding' && 'No Roll-off or Completed candidates available for offboarding survey'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {getEligibleCandidates().map((candidate) => (
                        <label
                          key={candidate.id}
                          className="flex items-center p-4 border-2 border-slate-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCandidates.includes(candidate.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedCandidates([...selectedCandidates, candidate.id]);
                              } else {
                                setSelectedCandidates(selectedCandidates.filter(id => id !== candidate.id));
                              }
                            }}
                            className="w-5 h-5 text-blue-600 rounded border-slate-300 focus:ring-2 focus:ring-blue-500"
                          />
                          <div className="ml-4 flex-1">
                            <p className="font-semibold text-slate-900">
                              {candidate.candidateName || candidate.candidate_name || candidate.name || 'Unknown Candidate'}
                            </p>
                            <p className="text-sm text-slate-500">
                              {candidate.candidateEmail || candidate.candidate_email || candidate.email || 'No email'}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border-t-2 border-slate-200 p-6 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-600">
                      {selectedCandidates.length} candidate(s) selected
                    </p>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => {
                          setShowSendModal(false);
                          setSelectedCandidates([]);
                        }}
                        className="px-4 py-2 border-2 border-slate-300 text-slate-700 rounded-xl hover:bg-slate-100 transition-all font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSendSurvey}
                        disabled={selectedCandidates.length === 0 || isLoading}
                        className="flex items-center px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {isLoading ? 'Sending...' : 'Send Survey'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* View Response Modal */}
          {showResponseModal && selectedSurvey && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-6 h-6 text-white" />
                      <h3 className="text-xl font-bold text-white">Survey Response</h3>
                    </div>
                    <button
                      onClick={() => {
                        setShowResponseModal(false);
                        setSelectedSurvey(null);
                      }}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(90vh-150px)]">
                  <div className="space-y-6">
                    {/* Candidate Info */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <h4 className="font-semibold text-slate-900 mb-3">Candidate Information</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-600">Name</p>
                          <p className="font-medium text-slate-900">{selectedSurvey.candidate_name}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">Email</p>
                          <p className="font-medium text-slate-900">{selectedSurvey.candidate_email}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">Completed Date</p>
                          <p className="font-medium text-slate-900">
                            {new Date(selectedSurvey.completed_date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Survey Responses */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Survey Responses</h4>
                      <SurveyResponseView 
                        surveyId={selectedSurvey.id}
                        API_BASE_URL={API_BASE_URL}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t-2 border-slate-200 p-6 bg-slate-50">
                  <button
                    onClick={() => {
                      setShowResponseModal(false);
                      setSelectedSurvey(null);
                    }}
                    className="w-full px-6 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Settings Tab - Question Manager */
        <SurveyQuestionManager
          API_BASE_URL={API_BASE_URL}
          success={success}
          error={error}
        />
      )}
    </div>
  );
};

// Survey Response View Component
const SurveyResponseView = ({ surveyId, API_BASE_URL }) => {
  const [responses, setResponses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchResponses();
  }, [surveyId]);

  const fetchResponses = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/surveys.php?action=getResponse&survey_id=${surveyId}`);
      const data = await response.json();
      
      if (data.success) {
        setResponses(data.responses || []);
      }
    } catch (err) {
      console.error('Error fetching responses:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-6">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {responses.map((response, idx) => (
        <div key={response.id} className="border-2 border-slate-200 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
              {idx + 1}
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-700 mb-2">{response.question_text}</p>
              
              {response.question_type === 'rating' && response.response_value && (
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Award 
                      key={i} 
                      className={`w-5 h-5 ${i < response.response_value ? 'text-yellow-500 fill-yellow-500' : 'text-slate-300'}`} 
                    />
                  ))}
                </div>
              )}
              
              {(response.question_type === 'radio' || response.question_type === 'checkbox') && (
                <div className="bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-900">
                  {response.response_text}
                </div>
              )}
              
              {(response.question_type === 'text' || response.question_type === 'textarea') && (
                <p className="text-sm text-slate-600">{response.response_text || 'No response provided'}</p>
              )}
            </div>
          </div>
        </div>
      ))}
      
      {responses.length === 0 && (
        <div className="text-center py-6 text-slate-500">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-400" />
          <p className="text-sm">No responses found</p>
        </div>
      )}
    </div>
  );
};

export default Survey;